/**
 * Shared tab identifiers for the Student card (`/students/:id/details`).
 *
 * Extracted into its own tiny module so presentational pieces (e.g.
 * `parts/StudentTabBar.tsx`) and the page shell (`StudentDetail.tsx`) can both
 * reference the same `TabId` union without a circular import between the shell
 * and its children. Mirrors `GroupDetail/tabIds.ts`.
 *
 * The same five-value union is also declared in `core/utils/studentAlerts.ts`
 * as `StudentTabId` (the pure-layer home, kept decoupled from the component
 * tree); the two definitions stay byte-for-byte in sync.
 */

export type TabId =
  | 'overview'
  | 'agreements'
  | 'payments'
  | 'invoices'
  | 'pandadoc'
  | 'communication'
  | 'tasks';

/** Canonical left-to-right tab order rendered by the tab bar. */
export const TAB_IDS: TabId[] = [
  'overview',
  'agreements',
  'payments',
  'invoices',
  'pandadoc',
  'communication',
  'tasks',
];

/** Narrowing guard for an untrusted string (e.g. a `?tab=` URL value). */
export const isTabId = (value: string | null): value is TabId =>
  value !== null && (TAB_IDS as string[]).includes(value);

/** DOM id for a tab control, used as the `aria-controls` target's pair. */
export const tabElementId = (tab: TabId): string => `student-tab-${tab}`;

/** DOM id for the panel a tab controls (`role="tabpanel"` owner). */
export const tabPanelId = (tab: TabId): string => `student-tabpanel-${tab}`;
